/**
 * IndexPopovers — Designer-side popover wrapper for the add index form.
 * The Index drop / edit affordances ship in later C-phase slices (drop in
 * C5; edit covers cumulative re-adds in subsequent C-phases). Mirrors the
 * `PrimaryKeyPopovers` pattern from Slice B1.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice C1.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { QualifiedName } from '../../../../sql-grammar/model';
import { isAddIndexSupported } from '../../edit/edits/addIndex';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { IndexAddFormBody } from './IndexAddFormBody';

export interface IndexAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
  /** Override target table (passed through to the form body). Caller
   *  resolves this from the parent structure when the parked statement
   *  doesn't carry a table directly. */
  readonly tableOverride?: QualifiedName | null;
}

export function IndexAddPopover(props: IndexAddPopoverProps) {
  const { editContext, testId = 'index-add-popover', onCommitted, tableOverride } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddIndexSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip="add-index unsupported on this engine"
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Index
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add index"
        >
          <Plus className="h-3.5 w-3.5" />
          Index
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="flex max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 flex-col overflow-hidden"
        data-testid={testId}
      >
        {open ? (
          <IndexAddFormBody
            editContext={editContext}
            testId={testId}
            tableOverride={tableOverride}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
